import { useState } from "react";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const CONTACT_INFO = [
  { icon: Mail, label: "Email Us", value: "info@viewna.co.in", href: "mailto:info@viewna.co.in", testid: "contact-info-email" },
  { icon: Phone, label: "Call Us", value: "+91 99208 29818", href: "tel:+919920829818", testid: "contact-info-phone" },
  { icon: MapPin, label: "Visit Us", value: "Mumbai, Maharashtra", href: null, testid: "contact-info-location" },
];

const INPUT_CLS =
  "w-full rounded-xl border border-stone-700/70 bg-ink-900/80 px-4 py-3 text-sm text-stone-100 placeholder:text-stone-500 transition-colors focus:border-gold-500/60 focus:outline-none focus:ring-1 focus:ring-gold-500/40";

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent successfully.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 lg:py-32">
      <div className="pointer-events-none absolute right-0 top-0 h-[420px] w-[420px] rounded-full bg-gold-500/5 blur-[130px]" />
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.5em] text-gold-500">
              <Mail className="h-4 w-4" /> Get in Touch
            </p>
            <h2 className="mt-4 font-display text-4xl font-black text-stone-50 sm:text-5xl">
              Contact <span className="gold-text">Us</span>
            </h2>
            <p className="mt-6 max-w-md leading-relaxed text-stone-400">
              Questions about a show, a partnership or your submission? The VIEWNA team is one message away.
            </p>

            <div className="mt-10 space-y-4">
              {CONTACT_INFO.map(({ icon: Icon, label, value, href, testid }) => {
                const content = (
                  <>
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-gold-500/30 bg-gold-500/10 text-gold-400 transition-colors duration-300 group-hover:bg-gold-500 group-hover:text-ink-950">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span>
                      <span className="block text-[10px] uppercase tracking-[0.3em] text-stone-500">{label}</span>
                      <span className="mt-1 block text-base font-medium text-stone-100">{value}</span>
                    </span>
                  </>
                );
                return href ? (
                  <a
                    key={testid}
                    href={href}
                    data-testid={testid}
                    className="group flex items-center gap-5 rounded-2xl border border-stone-800/80 bg-ink-900/40 p-5 transition-all duration-300 hover:border-gold-500/40 hover:bg-ink-900/70"
                  >
                    {content}
                  </a>
                ) : (
                  <div key={testid} data-testid={testid} className="group flex items-center gap-5 rounded-2xl border border-stone-800/80 bg-ink-900/40 p-5">
                    {content}
                  </div>
                );
              })}
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-3xl border border-gold-500/20 bg-ink-900/50 p-7 sm:p-9"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="mb-2 block text-[10px] uppercase tracking-[0.3em] text-stone-400">Name *</label>
                <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={INPUT_CLS} />
              </div>
              <div>
                <label htmlFor="contact-email" className="mb-2 block text-[10px] uppercase tracking-[0.3em] text-stone-400">Email *</label>
                <input id="contact-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" className={INPUT_CLS} />
              </div>
              <div>
                <label htmlFor="contact-phone" className="mb-2 block text-[10px] uppercase tracking-[0.3em] text-stone-400">Phone</label>
                <input id="contact-phone" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={INPUT_CLS} />
              </div>
              <div>
                <label htmlFor="contact-subject" className="mb-2 block text-[10px] uppercase tracking-[0.3em] text-stone-400">Subject</label>
                <input id="contact-subject" data-testid="contact-subject-input" value={form.subject} onChange={set("subject")} placeholder="What is this about?" className={INPUT_CLS} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="contact-message" className="mb-2 block text-[10px] uppercase tracking-[0.3em] text-stone-400">Message *</label>
                <textarea id="contact-message" data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")} placeholder="Tell us more..." className={`${INPUT_CLS} resize-none`} />
              </div>
            </div>
            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={sending}
              className="mt-7 inline-flex w-full items-center justify-center gap-3 rounded-full bg-gradient-to-r from-gold-600 via-gold-400 to-gold-600 bg-[length:200%_100%] px-9 py-4 text-[13px] font-bold uppercase tracking-[0.2em] text-ink-950 transition-all duration-500 hover:bg-right hover:shadow-[0_0_36px_rgba(212,175,55,0.4)] disabled:opacity-60 sm:w-auto"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {sending ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
